import { useEffect, useId, useState, type ReactNode } from 'react';

import { useCopy } from '../lib/use-copy';
import { FieldLabel } from './field-label';
import { CheckIcon, CopyIcon } from './icons';
import { Input } from './input';
import { Modal } from './modal';
import { Note } from './note';

interface IConfirmDeleteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: string;
  name: string;
  verb?: string;
  consequences?: string;
  note?: ReactNode;
  waiting?: boolean;
  onConfirm: () => void;
}

// Typing the name is the one check a misclick cannot pass. The confirm button
// is the ordinary primary: tokens.css keeps red for data state alone.
export const ConfirmDelete = ({
  open,
  onOpenChange,
  entity,
  name,
  verb = 'Delete',
  consequences,
  note,
  waiting,
  onConfirm,
}: IConfirmDeleteProps) => {
  const [typed, setTyped] = useState('');
  const { copied, copy } = useCopy();
  const inputId = useId();

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const matches = typed.trim() === name.trim();
  const action = `${verb} ${entity}`;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={action}
      submit={{
        label: action,
        onSubmit: onConfirm,
        disabled: !matches,
        waiting,
        waitingLabel: `${verb.replace(/e$/, '')}ing`,
      }}
    >
      <div className="flex flex-col gap-4 pt-2">
        <p className="text-data text-muted">
          This permanently {verb.toLowerCase()}s{' '}
          <button
            type="button"
            title={copied ? 'Copied' : 'Copy name'}
            onClick={() => void copy(name)}
            className="inline-flex items-center gap-1 rounded-tag bg-ghost px-label align-middle font-mono text-meta text-primary"
          >
            {name}
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
          {consequences && (
            <>
              {' '}
              with <span className="font-mono text-primary">{consequences}</span>
            </>
          )}
          . This cannot be undone.
        </p>

        {note && <Note tone="danger">{note}</Note>}

        <div>
          <FieldLabel htmlFor={inputId}>Type the {entity} name to confirm</FieldLabel>
          <Input
            id={inputId}
            mono
            autoFocus
            autoComplete="off"
            spellCheck={false}
            value={typed}
            placeholder={name}
            onChange={(event) => setTyped(event.target.value)}
            // Without modifiers, or ⌘↵ fires here AND in the dialog: two deletes.
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || event.metaKey || event.ctrlKey) return;
              if (!matches || waiting) return;
              event.preventDefault();
              onConfirm();
            }}
          />
        </div>
      </div>
    </Modal>
  );
};
